import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  updateDeliverableData,
  createDeliverable,
  deleteDeliverable,
  fetchDeliverables,
} from "../../../../store/features/deliverable/deliverableSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { fetchStaffsByUser } from "../../../../store/features/user/userSlice";
import { fetchResourceAllocations } from "../../../../store/features/resourceAllocation/resourceAllocationSlice";
import {
  fetchWorkPackageById,
  fetchWorkPackageByProjectId,
} from "../../../../store/features/workPackage/workPackageSlice";
import { formatDate } from "date-fns";

const DeliverableFormData = ({
  deliverablesInfo = {},
  setDeliverablesInfo,
  setworkPackageById,
  setShowDeliverableModal,
  showDeliverableModal,
}) => {
  const { projects, projectById } = useSelector((state) => state.projects);
  const dispatch = useDispatch();
  const [managersOptions, setManagersOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedWpId, setSelectedWpId] = useState(null);
  const [selectAsssignTo, setSelectAsssignTo] = useState(null);
  const [projectId, setProjectId] = useState(projectById._id || "");
  const [getAssistId, setGetAssistId] = useState(null);

  const { resourceAllocations } = useSelector(
    (state) => state.resourceAllocations,
  );
  const {
    usersCreatedBy = [],
    staffsByUser = [],
    managers = [],
  } = useSelector((state) => state.users);
  const { workPackageById, workPackages, workPackagesByProjectId } =
    useSelector((state) => state.workPackages);

  const { role } = useSelector((state) => state.auth);

  const initialValues = {
    title: deliverablesInfo.title || "",
    dlvNumber: deliverablesInfo.dlvNumber || "",
    cost: deliverablesInfo.cost || "",
    description: deliverablesInfo.description || "",
    notes: deliverablesInfo.notes || "",
    addNotes: !!deliverablesInfo.notes,
    assignedTo: deliverablesInfo.assignedTo || "",
    assignedBy: deliverablesInfo.assignedBy || "",
    startDate: deliverablesInfo.startDate
      ? deliverablesInfo.startDate.slice(0, 10)
      : "",
    endDate: deliverablesInfo.endDate
      ? deliverablesInfo.endDate.slice(0, 10)
      : "",
    deliverablesFiles: deliverablesInfo.deliverablesFiles || [],
    status: deliverablesInfo.status || "",
    approvalStatus: "pending",
    rejectionReason: "",
    progressLevel: 0,
    wpId: workPackageById._id || "",
    role,
  };

  const getValidationSchema = (workPackages) =>
    Yup.object().shape({
      title: Yup.string().required("Deliverable title is required"),
      wpId: Yup.string().required("Workpackage name is required"),
      assignedTo: Yup.string().required("Assigned to name is required"),
      status: Yup.string().required("Status is required"),
      cost: Yup.string().required("Cost is required"),
      dlvNumber: Yup.string().required("Number is required"),
      description: Yup.string().required("Description is required"),
      startDate: Yup.date()
        .required("Start date is required")
        .when("wpId", (wpId, schema) => {
          if (wpId && workPackages) {
            const wp = workPackages.find((w) => w._id === wpId[0]);
            if (wp?.startDate && wp?.endDate) {
              const startDateStr =
                typeof wp.startDate === "string"
                  ? wp.startDate.slice(0, 10)
                  : null;
              const endDateStr =
                typeof wp.endDate === "string" ? wp.endDate.slice(0, 10) : null;
              return schema
                .test(
                  "is-within-wp-start",
                  `Start date must be on or after workpackage start (${startDateStr || "N/A"})`,
                  function (value) {
                    if (!value) return true;
                    const inputDate = new Date(value);
                    inputDate.setHours(0, 0, 0, 0);
                    const wpStart = new Date(wp.startDate);
                    wpStart.setHours(0, 0, 0, 0);
                    return inputDate >= wpStart;
                  },
                )
                .test(
                  "is-within-wp-end",
                  `Start date must be on or before workpackage end (${endDateStr || "N/A"})`,
                  function (value) {
                    if (!value) return true;
                    const inputDate = new Date(value);
                    inputDate.setHours(0, 0, 0, 0);
                    const wpEnd = new Date(wp.endDate);
                    wpEnd.setHours(0, 0, 0, 0);
                    return inputDate <= wpEnd;
                  },
                );
            }
          }
          return schema;
        }),

      endDate: Yup.date()
        .required("End date is required")
        .test(
          "is-same-or-after-start",
          "End date must be on or after start date",
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            const inputEndDate = new Date(value);
            inputEndDate.setHours(0, 0, 0, 0);
            const inputStartDate = new Date(startDate);
            inputStartDate.setHours(0, 0, 0, 0);
            return inputEndDate >= inputStartDate;
          },
        )
        .when("wpId", (wpId, schema) => {
          if (wpId && workPackages) {
            const wp = workPackages.find((w) => w._id === wpId[0]);
            if (wp?.endDate) {
              const endDateStr =
                typeof wp.endDate === "string" ? wp.endDate.slice(0, 10) : null;
              return schema.test(
                "is-within-wp-end",
                `End date must be on or before workpackage end (${endDateStr || "N/A"})`,
                function (value) {
                  if (!value) return true;
                  const inputDate = new Date(value);
                  inputDate.setHours(0, 0, 0, 0);
                  const wpEnd = new Date(wp.endDate);
                  wpEnd.setHours(0, 0, 0, 0);
                  return inputDate <= wpEnd;
                },
              );
            }
          }
          return schema;
        }),
    });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      values.projectId =
        workPackageById.projectId || deliverablesInfo?.projectId;
      values.assistantSuperAdminId = workPackageById.assistantSuperAdminId;
      values.wpId = workPackageById._id || deliverablesInfo?.wpId;

      // Prepare FormData
      const formData = new FormData();
      for (let key in values) {
        if (
          key === "deliverablesFiles" &&
          Array.isArray(values.deliverablesFiles)
        ) {
          values.deliverablesFiles.forEach((file) => {
            formData.append("deliverablesFiles", file);
          });
        } else if (key !== "addNotes") {
          if (key === "notes" && !values.addNotes) {
            continue;
          }
          formData.append(key, values[key]);
        }
      }

      if (deliverablesInfo._id) {
        formData.append("_id", deliverablesInfo._id);
        await dispatch(updateDeliverableData(formData)).unwrap();
        toast.success("Deliverable updated!");
      } else {
        await dispatch(createDeliverable(formData)).unwrap();
        toast.success("Deliverable created!");
      }

      await dispatch(fetchDeliverables());
      resetForm();
      setDeliverablesInfo({});
      setShowDeliverableModal(false);
    } catch (err) {
      toast.error(err?.message || "Error saving Deliverable");
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    "Not Started",
    "In Progress",
    "Submitted",
    "Completed",
  ];

  // Fetch managers and resources
  useEffect(() => {
    const getAssistId = projects?.find(
      (p) => p._id === projectId,
    )?.assistantSuperAdminId;
    setGetAssistId(getAssistId);
    if (getAssistId) {
      dispatch(fetchStaffsByUser(getAssistId));
      dispatch(fetchResourceAllocations());
    } else {
      setManagersOptions([]);
    }
  }, [projectId, dispatch]);

  // Compute unique managers from multiple sources
  useEffect(() => {
    if (!getAssistId) return;

    const allocation = resourceAllocations.find(
      (ra) => ra.assistantSuperadmin?._id === getAssistId,
    );

    const allocStaffs = allocation?.resource?.flatMap((r) => r.staffs) || [];

    const apiStaffs = Array.isArray(staffsByUser)
      ? staffsByUser[0]?.createdUsers || []
      : [];

    const combinedStaffs = [...allocStaffs, ...apiStaffs];
    const uniqueStaffs = Array.from(
      new Map(combinedStaffs.map((m) => [m?._id?.toString(), m])).values(),
    );

    setStaffOptions(uniqueStaffs);
  }, [resourceAllocations, usersCreatedBy, staffsByUser, getAssistId]);

  useEffect(() => {
    if (projectId || deliverablesInfo.projectId) {
      dispatch(
        fetchWorkPackageByProjectId(deliverablesInfo.projectId || projectId),
      );
    }

    if (selectedWpId || deliverablesInfo.wpId) {
      dispatch(fetchWorkPackageById(deliverablesInfo.wpId || selectedWpId));
    }
  }, [
    projectId,
    dispatch,
    deliverablesInfo.projectId,
    selectedWpId,
    deliverablesInfo.wpId,
  ]);

  useEffect(() => {
    if (deliverablesInfo?.assignedUser?.role) {
      setSelectAsssignTo(deliverablesInfo?.assignedUser?.role);
    }
  }, [deliverablesInfo]);

  return (
    <Modal
      size="lg"
      show={showDeliverableModal}
      onHide={() => setShowDeliverableModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {deliverablesInfo._id ? "Update" : "Add"} Deliverable
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={getValidationSchema(workPackagesByProjectId)}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                setFieldValue,
                touched,
                errors,
                isSubmitting,
                resetForm,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Project Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="projectId"
                          value={values.projectId || deliverablesInfo.projectId}
                          onChange={(e) => {
                            handleChange(e);
                            setProjectId(e.currentTarget.value);
                          }}
                          isInvalid={touched.projectId && !!errors.projectId}
                        >
                          <option value="">Select Project</option>
                          {projects?.map((project) => (
                            <option key={project._id} value={project._id}>
                              {project.projectName}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.projectId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Work package <span className="text-danger">*</span>
                        </Form.Label>

                        <Form.Select
                          name="wpId"
                          value={values.wpId}
                          onChange={(e) => {
                            handleChange(e);
                            setSelectedWpId(e.currentTarget.value);
                          }}
                          isInvalid={touched.wpId && !!errors.wpId}
                        >
                          <option value="">Select Workpackage</option>
                          {workPackagesByProjectId?.map((wp) => (
                            <option key={wp._id} value={wp._id}>
                              {wp.title}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.wpId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Deliverable Title */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          isInvalid={touched.title && errors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.title}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Description */}
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Description <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          as="textarea"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          isInvalid={touched.description && errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.description}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Assigned to <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          onChange={(e) => setSelectAsssignTo(e.target.value)}
                          value={deliverablesInfo?.assignedUser?.role}
                        >
                          <option value="manager">Manager</option>
                          <option value="staff">Staff</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.assignedTo}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {selectAsssignTo === "manager" && (
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Manager <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            name="assignedTo"
                            value={values.assignedTo}
                            onChange={handleChange}
                            isInvalid={touched.assignedTo && errors.assignedTo}
                          >
                            <option value="">Select</option>
                            {workPackageById?.managerIds &&
                              managers
                                ?.filter(
                                  (m) =>
                                    m._id === workPackageById?.managerIds[0],
                                )
                                ?.map((manager) => (
                                  <option key={manager._id} value={manager._id}>
                                    {manager.name}
                                  </option>
                                ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.assignedTo}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}

                    {selectAsssignTo === "staff" && (
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Staff <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            name="assignedTo"
                            value={values.assignedTo}
                            onChange={handleChange}
                            isInvalid={touched.assignedTo && errors.assignedTo}
                          >
                            <option value="">Select</option>
                            {staffOptions
                              ?.filter((staff) => staff?._id && staff?.name)
                              .map((staff) => (
                                <option key={staff._id} value={staff._id}>
                                  {staff.name}
                                </option>
                              ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.assignedTo}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Deliverable Number
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="dlvNumber"
                          value={values.dlvNumber}
                          onChange={handleChange}
                          isInvalid={touched.dlvNumber && errors.dlvNumber}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.dlvNumber}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Cost <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="cost"
                          value={values.cost}
                          onChange={handleChange}
                          isInvalid={touched.cost && errors.cost}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cost}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* End Date */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Start Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={values.startDate}
                          onChange={handleChange}
                          isInvalid={touched.startDate && errors.startDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.startDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* End Date */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          End Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={values.endDate}
                          onChange={handleChange}
                          isInvalid={touched.endDate && errors.endDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.endDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Status */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Status
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="status"
                          value={values.status}
                          onChange={handleChange}
                          isInvalid={touched.status && errors.status}
                        >
                          <option value="">Select</option>
                          {statusOptions?.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.status}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Attachments */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Attachments</Form.Label>
                        <Form.Control
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.currentTarget.files);
                            setFieldValue("deliverablesFiles", files);
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="checkbox"
                        label="Add Notes"
                        name="addNotes"
                        checked={values.addNotes}
                        onChange={(e) =>
                          setFieldValue("addNotes", e.target.checked)
                        }
                        className="mb-2"
                      />
                      {values.addNotes && (
                        <Form.Group className="mb-3">
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="notes"
                            value={values.notes}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-end">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          resetForm();
                          setShowDeliverableModal(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="ms-2"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {deliverablesInfo.title ? "Update" : "Create"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default DeliverableFormData;
